<template>
  <Message>
    <template v-for="(_, slotName) in $slots" #[slotName]="slotProps">
      <slot :name="slotName" v-bind="slotProps ?? {}" />
    </template>
  </Message>
</template>

<script setup lang="ts">
import type { MessageEmits, MessageProps } from "primevue";

import { Message } from "primevue";

defineProps</* @vue-ignore */ MessageProps>();
defineEmits</* @vue-ignore */ MessageEmits>();
</script>

<style lang="postcss">
.p-message {
  @apply rounded-md outline outline-1;

  &-content {
    @apply flex h-full items-center px-3 py-2 gap-2;
  }

  &-icon {
    @apply h-[1.125rem] w-[1.125rem] flex-shrink-0 text-lg;
  }

  &-close-button {
    @apply relative ms-auto flex h-7 w-7 flex-shrink-0 cursor-pointer select-none items-center justify-center overflow-hidden rounded-full border-none bg-transparent p-0 text-inherit transition-colors duration-200 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2;
  }

  &-info {
    @apply bg-primary-50 text-primary-600 shadow-[0px_4px_8px_0px_theme(colors.primary.500/0.04)] outline-primary-200;

    .p-message-close-button {
      @apply hover:bg-primary-100 focus-visible:outline-primary-600;
    }

    &.p-message-outlined {
      @apply text-primary-500 outline-primary-500;
    }

    &.p-message-simple {
      @apply text-primary-500;
    }
  }

  &-success {
    @apply bg-green-50/95 text-green-600 shadow-[0px_4px_8px_0px_theme(colors.green.500/0.04)] outline-green-200;

    .p-message-close-button {
      @apply hover:bg-green-100 focus-visible:outline-green-600;
    }

    &.p-message-outlined {
      @apply text-green-500 outline-green-500;
    }

    &.p-message-simple {
      @apply text-green-500;
    }
  }

  &-warn {
    @apply bg-warning-50/95 text-warning-700 shadow-[0px_4px_8px_0px_theme(colors.warning.500/0.04)] outline-warning-200;

    .p-message-close-button {
      @apply hover:bg-warning-100 focus-visible:outline-warning-600;
    }

    &.p-message-outlined {
      @apply text-warning-500 outline-warning-500;
    }

    &.p-message-simple {
      @apply text-yellow-500;
    }
  }

  &-error {
    @apply bg-red-50/95 text-red-600 shadow-[0px_4px_8px_0px_theme(colors.red.500/0.04)] outline-red-200;

    .p-message-close-button {
      @apply hover:bg-red-100 focus-visible:outline-red-600;
    }

    &.p-message-outlined {
      @apply text-red-500 outline-red-500;
    }

    &.p-message-simple {
      @apply text-red-500;
    }
  }

  &-secondary {
    @apply bg-surface-100 text-surface-600 shadow-[0px_4px_8px_0px_rgba(0,0,0,0.04)] outline-surface-200;

    .p-message-close-button {
      @apply hover:bg-surface-200 focus-visible:outline-surface-600;
    }

    &.p-message-outlined {
      @apply text-surface-500 outline-surface-500;
    }

    &.p-message-simple {
      @apply text-surface-500;
    }
  }

  &-contrast {
    @apply bg-surface-900 text-surface-50 shadow-[0px_4px_8px_0px_rgba(0,0,0,0.04)] outline-surface-950;

    .p-message-close-button {
      @apply hover:bg-surface-800 focus-visible:outline-surface-50;
    }

    &.p-message-outlined {
      @apply text-surface-950 outline-surface-950;
    }

    &.p-message-simple {
      @apply text-surface-950;
    }
  }

  &-text {
    @apply text-base font-medium;
  }

  &-enter-from {
    @apply opacity-0;
  }

  &-enter-active {
    @apply transition-opacity duration-300;
  }

  &&-leave-from {
    @apply max-h-[1000px];
  }

  &&-leave-to {
    @apply m-0 max-h-0 opacity-0;
  }

  &-leave-active {
    overflow: hidden;
    transition:
      max-height 0.45s cubic-bezier(0, 1, 0, 1),
      opacity 0.3s,
      margin 0.3s;

    .p-message-close-button {
      @apply opacity-0;
    }
  }

  &-sm {
    .p-message-content {
      @apply px-2.5 py-[0.375rem];
    }

    .p-message-text {
      @apply text-sm;
    }

    .p-message-icon {
      @apply h-4 w-4 text-sm;
    }

    .p-message-close-icon {
      @apply h-3.5 w-3.5 text-sm;
    }
  }

  &-lg {
    .p-message-content {
      @apply px-3.5 py-2.5;
    }

    .p-message-text {
      @apply text-xl;
    }

    .p-message-icon {
      @apply h-5 w-5 text-xl;
    }

    .p-message-close-icon {
      @apply h-[1.125rem] w-[1.125rem] text-xl;
    }
  }

  &-outlined {
    @apply bg-transparent outline outline-1;

    .p-message-close-button:hover {
      @apply bg-transparent;
    }
  }

  &-simple {
    @apply bg-transparent shadow-none outline-none;

    .p-message-content {
      @apply p-0;
    }

    .p-message-close-button:hover {
      @apply bg-transparent;
    }
  }
}
</style>
